import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, RefreshCcw, Save, Video } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminLiveLapang = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [venues, setVenues] = useState([]);
  const [rtspMap, setRtspMap] = useState({});
  const [intervalMinutes, setIntervalMinutes] = useState(30);
  const [mode, setMode] = useState('live');
  const [historyDate, setHistoryDate] = useState(() => {
    try {
      return new Date().toLocaleDateString('en-CA');
    } catch (_) {
      return new Date().toISOString().slice(0, 10);
    }
  });
  const [historyTime, setHistoryTime] = useState(() => {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });
  const [snapshots, setSnapshots] = useState({});
  const snapshotUrlsRef = useRef({});

  const upsertSnapshotState = useCallback((venueId, patch) => {
    setSnapshots((prev) => ({
      ...prev,
      [venueId]: {
        ...(prev[venueId] || {}),
        ...patch,
      },
    }));
  }, []);

  const formatBytes = (bytes) => {
    const b = Number(bytes || 0);
    if (!Number.isFinite(b) || b <= 0) return '0 B';
    const k = 1024;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(k)));
    const v = b / Math.pow(k, i);
    const digits = i === 0 ? 0 : v >= 100 ? 0 : v >= 10 ? 1 : 2;
    return `${v.toFixed(digits)} ${units[i]}`;
  };

  const folderPercent = (folderBytes) => {
    const total = Number(uploadStatus?.disk?.total || 0);
    const b = Number(folderBytes || 0);
    if (!Number.isFinite(total) || total <= 0) return 0;
    if (!Number.isFinite(b) || b <= 0) return 0;
    return Math.min(100, Math.max(0, (b / total) * 100));
  };

  const fetchSnapshot = useCallback(async (venueId, force) => {
    upsertSnapshotState(venueId, { loading: true, error: null });
    try {
      const res = await api.get(`/admin/cctv/snapshot/${venueId}`, {
        responseType: 'blob',
        params: force ? { force: 1 } : undefined,
      });

      const nextUrl = URL.createObjectURL(res.data);
      const prevUrl = snapshotUrlsRef.current[venueId];
      snapshotUrlsRef.current[venueId] = nextUrl;
      if (prevUrl) {
        try {
          URL.revokeObjectURL(prevUrl);
        } catch (_) {}
      }

      upsertSnapshotState(venueId, {
        url: nextUrl,
        updatedAt: Date.now(),
        loading: false,
        error: null,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Gagal ambil snapshot';
      upsertSnapshotState(venueId, {
        loading: false,
        error: msg,
      });
    }
  }, [upsertSnapshotState]);

  const fetchHistorySnapshot = useCallback(async (venueId, atIso) => {
    upsertSnapshotState(venueId, { loading: true, error: null });
    try {
      const res = await api.get(`/admin/cctv/history/${venueId}/at`, {
        responseType: 'blob',
        params: { at: atIso },
      });

      const nextUrl = URL.createObjectURL(res.data);
      const prevUrl = snapshotUrlsRef.current[venueId];
      snapshotUrlsRef.current[venueId] = nextUrl;
      if (prevUrl) {
        try {
          URL.revokeObjectURL(prevUrl);
        } catch (_) {}
      }

      upsertSnapshotState(venueId, {
        url: nextUrl,
        updatedAt: Date.now(),
        loading: false,
        error: null,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Snapshot tidak ditemukan';
      upsertSnapshotState(venueId, {
        loading: false,
        error: msg,
      });
    }
  }, [upsertSnapshotState]);

  const refreshAll = useCallback(async (force) => {
    if (!venues.length) return;
    setRefreshing(true);
    try {
      for (const v of venues) {
        await fetchSnapshot(v.id, force);
      }
    } finally {
      setRefreshing(false);
    }
  }, [venues, fetchSnapshot]);

  const viewHistoryAll = useCallback(async () => {
    if (!venues.length) return;
    const date = (historyDate || '').trim();
    const time = (historyTime || '').trim();
    if (!date || !time) {
      toast.error('Pilih tanggal dan jam terlebih dulu');
      return;
    }
    const atIso = `${date}T${time}:00`;
    setRefreshing(true);
    try {
      for (const v of venues) {
        await fetchHistorySnapshot(v.id, atIso);
      }
    } finally {
      setRefreshing(false);
    }
  }, [venues, historyDate, historyTime, fetchHistorySnapshot]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [venuesRes, cfgRes, uploadsRes] = await Promise.all([
        api.get('/venues'),
        api.get('/admin/cctv/config'),
        api.get('/admin/uploads/status'),
      ]);

      const venuesList = Array.isArray(venuesRes.data) ? venuesRes.data : [];
      setVenues(venuesList);

      const cfg = cfgRes.data || {};
      setRtspMap(cfg.rtsp_map || {});
      setIntervalMinutes(cfg.default_interval_minutes || 30);
      setUploadStatus(uploadsRes.data || null);
    } catch (_) {
      toast.error('Gagal memuat data Live Lapang');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        toast.error('Akses ditolak');
        navigate('/admin/login');
        return;
      }
    } catch (_) {
      toast.error('Akses ditolak');
      navigate('/admin/login');
      return;
    }

    loadAll();
  }, [navigate, loadAll]);

  useEffect(() => {
    return () => {
      const urls = snapshotUrlsRef.current;
      for (const k of Object.keys(urls)) {
        try {
          URL.revokeObjectURL(urls[k]);
        } catch (_) {}
      }
      snapshotUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (mode !== 'live') return;
    if (!venues.length) return;
    const ms = Math.max(1, intervalMinutes) * 60 * 1000;
    const id = window.setInterval(() => {
      refreshAll(false);
    }, ms);
    return () => window.clearInterval(id);
  }, [mode, venues, intervalMinutes, refreshAll]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(rtspMap || {})) {
        if (typeof k !== 'string' || typeof v !== 'string') continue;
        const url = v.trim();
        if (!url) continue;
        if (!url.startsWith('rtsp://') && !url.startsWith('rtsps://')) continue;
        cleaned[k] = url;
      }

      await api.post('/admin/cctv/config', {
        rtsp_map: cleaned,
        default_interval_minutes: intervalMinutes,
      });
      toast.success('Konfigurasi Live Lapang tersimpan');
    } catch (_) {
      toast.error('Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div className="flex items-center gap-3">
                <Video className="w-6 h-6 text-primary" />
                <span className="text-xl font-heading font-bold italic text-white uppercase">Live Lapang</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => (mode === 'live' ? refreshAll(true) : viewHistoryAll())}
                disabled={loading || refreshing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-white hover:border-primary/50 disabled:opacity-50"
              >
                <RefreshCcw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={saveConfig}
                disabled={loading || saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-black font-bold hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                Simpan
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            {uploadStatus?.folders ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-bold text-white uppercase font-heading">Bukti Bayar</div>
                    <div className="text-sm text-zinc-400">
                      {formatBytes(uploadStatus.folders.payments?.bytes)} • {uploadStatus.folders.payments?.files || 0} file
                    </div>
                  </div>
                  <div className="h-3 w-full rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${folderPercent(uploadStatus.folders.payments?.bytes)}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">uploads/payments</div>
                </div>

                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-bold text-white uppercase font-heading">Snapshot CCTV</div>
                    <div className="text-sm text-zinc-400">
                      {formatBytes(uploadStatus.folders.cctv_snapshots?.bytes)} • {uploadStatus.folders.cctv_snapshots?.files || 0} file
                    </div>
                  </div>
                  <div className="h-3 w-full rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-secondary"
                      style={{ width: `${folderPercent(uploadStatus.folders.cctv_snapshots?.bytes)}%` }}
                    />
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">uploads/cctv_snapshots</div>
                </div>
              </div>
            ) : null}

            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="text-sm text-zinc-400 font-semibold uppercase">Mode</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Live untuk snapshot terbaru. History untuk melihat snapshot pada tanggal & jam tertentu.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setMode('live')}
                    className={`px-4 py-2 rounded-md border ${mode === 'live' ? 'bg-primary text-black border-primary' : 'bg-zinc-900 text-white border-zinc-800 hover:border-primary/50'}`}
                  >
                    Live
                  </button>
                  <button
                    onClick={() => setMode('history')}
                    className={`px-4 py-2 rounded-md border ${mode === 'history' ? 'bg-primary text-black border-primary' : 'bg-zinc-900 text-white border-zinc-800 hover:border-primary/50'}`}
                  >
                    History
                  </button>
                </div>
              </div>

              {mode === 'live' ? (
                <div className="mt-5 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="text-sm text-zinc-400 font-semibold uppercase">Interval Snapshot (menit)</div>
                    <div className="text-xs text-zinc-500 mt-1">Default 30 menit. Bisa diganti sesuai kebutuhan.</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="1440"
                      value={intervalMinutes}
                      onChange={(e) => setIntervalMinutes(Number(e.target.value || 30))}
                      className="w-28 bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                    />
                    <button
                      onClick={() => refreshAll(false)}
                      disabled={refreshing}
                      className="px-4 py-2 rounded-md bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                    >
                      Terapkan
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-5 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1">
                    <div className="text-sm text-zinc-400 font-semibold uppercase">Pilih Tanggal & Jam</div>
                    <div className="text-xs text-zinc-500 mt-1">Menampilkan snapshot terakhir sebelum/tepat pada waktu yang dipilih.</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="date"
                      value={historyDate}
                      onChange={(e) => setHistoryDate(e.target.value)}
                      className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                    />
                    <input
                      type="time"
                      value={historyTime}
                      onChange={(e) => setHistoryTime(e.target.value)}
                      className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                    />
                    <button
                      onClick={viewHistoryAll}
                      disabled={refreshing}
                      className="px-4 py-2 rounded-md bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                    >
                      Lihat
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {venues.map((v) => {
                const snap = snapshots[v.id] || {};
                const updatedAt = snap.updatedAt
                  ? new Date(snap.updatedAt).toLocaleString('id-ID')
                  : '-';
                const rtspValue = rtspMap?.[v.id] || '';
                return (
                  <div key={v.id} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-zinc-800">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-white font-bold uppercase font-heading truncate">{v.name}</div>
                          <div className="text-xs text-zinc-500 mt-1">Terakhir update: {updatedAt}</div>
                        </div>
                        <button
                          onClick={() => fetchSnapshot(v.id, true)}
                          disabled={!!snap.loading}
                          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
                        >
                          <RefreshCcw className="w-4 h-4" />
                          Ambil
                        </button>
                      </div>
                      <div className="mt-3">
                        <div className="text-xs text-zinc-400 font-semibold uppercase">RTSP</div>
                        <input
                          value={rtspValue}
                          onChange={(e) =>
                            setRtspMap((prev) => ({
                              ...(prev || {}),
                              [v.id]: e.target.value,
                            }))
                          }
                          placeholder="rtsp://user:pass@ip:554/..."
                          className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary mt-2"
                        />
                        <div className="text-xs text-zinc-500 mt-2">
                          Jika RTSP belum diisi, snapshot akan gagal.
                        </div>
                      </div>
                    </div>

                    <div className="aspect-video bg-black flex items-center justify-center">
                      {snap.url ? (
                        <img
                          src={snap.url}
                          alt={`Snapshot ${v.name}`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="text-zinc-500 text-sm px-4 text-center">
                          {snap.loading ? 'Mengambil snapshot...' : (snap.error || 'Belum ada snapshot')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminLiveLapang;
